import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  Output,
  EventEmitter,
  HostBinding,
  NgZone,
  ViewChild,
} from '@angular/core'
import mapboxgl from 'mapbox-gl'
import { bboxPolygon } from 'app/utils/geo'
import carto from '@carto/carto-vl'
import { ContextService } from '../../services'
import { HttpClient } from '@angular/common/http'
import { firstValueFrom } from 'rxjs'

export interface ILayerConfig {
  /** Data source for this layer (string or JSON) */
  data: any
  /** Type of layer (fill, fill-extrude, line, symbol, ...) */
  type: string
  /** Optional. Base layer needed for vector tile sources */
  sourceLayer?: string
  /** Optional. Layer layout definition */
  layout?: any
  /** Optional. Layer styles definition. It depends on the layer type. */
  styles?: any
  /** Optional. Filter to apply to the layer elements. Follow Mapbox GL JS documentation */
  filter?: any[]
  /** Optional. Reorder this new layer before the one defined by this ID */
  before?: string
}

export interface ICartoLayerConfig {
  id: string
  viz: any
  source: any
  events?: Record<string, Function>
  before: string
}

export interface IMVTSourceConfig {
  url: string
  layerName?: string
  idProperty?: string
  properties?: any
  isAggregated?: boolean
}

export interface ICartoVLModel {
  layer: any
  interactivity: any
  events: Record<string, Function>
}

export type ILayerTree = Record<string, ICartoVLModel>

@Component({
  standalone: false,
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
})
export class MapComponent implements AfterViewInit {
  map: any

  @ViewChild('mapContainer', { static: false }) mapContainer: ElementRef

  @HostBinding('class.positron')
  positron =
    this.context.currentBasemap && this.context.currentBasemap.id === 'positron'

  private popupHover: any
  private popupClick: any
  private layersQueue: any[] = []
  private currentStyle: any = '/assets/styles/darkmatter.json'

  isMapLoaded = false
  layersTree: ILayerTree = {}

  @Input() center: number[] = [0, 0]
  @Input() authorization: string
  @Input() zoom = 0
  @Input() container = 'map'

  @Input()
  set styles(style) {
    this.currentStyle = style
    if (this.map) {
      this.setMapStyle(this.currentStyle)
    }
  }

  @Output() mapLoaded = new EventEmitter()
  @Input() mapBbox

  constructor(
    private context: ContextService,
    private http: HttpClient,
    private zone: NgZone,
  ) {}

  ngAfterViewInit() {
    const containerEl = this.mapContainer?.nativeElement
    if (!containerEl) {
      return
    }

    this.zone.runOutsideAngular(() => {
      const mapOptions = {
        container: containerEl,
        style: this.currentStyle,
        center: this.center,
        zoom: this.zoom,
        failIfMajorPerformanceCavea: true,
        preserveDrawingBuffer: true,
        transformRequest: (url) => {
          if (url.indexOf('/proxy/') > -1 && this.authorization !== undefined) {
            return {
              url: url,
              headers: { Authorization: this.authorization },
            }
          }
        },
      }

      this.map = new mapboxgl.Map(mapOptions)
      if (this.mapBbox && this.mapBbox.length >= 4) {
        this.map.fitBounds(this.mapBbox, {
          duration: 0,
          linear: true,
          padding: 150,
        })
      }

      this.map.dragRotate.disable()
      this.map.touchZoomRotate.disableRotation()

      this.map.on('load', () => {
        if (this.mapBbox && this.mapBbox.length >= 4) {
          this.map.fitBounds(this.mapBbox, {
            duration: 0,
            linear: true,
            padding: 150,
          })
        }

        while (this.layersQueue.length) {
          const layer = this.layersQueue.pop()
          this.loadLayer(layer)
        }

        this.map.on('style.load', (event) => {
          this.mapLoaded.emit(event)
        })

        setTimeout(() => {
          this.zone.run(() => {
            this.isMapLoaded = true
            this.mapLoaded.emit()
          })
        }, 100)
      })
    })
  }

  getLayer(id: string) {
    return (
      this.map.getLayer(id) && this.layersTree[id] && this.layersTree[id].layer
    )
  }

  setupLayerCursor(layerId: string) {
    this.zone.runOutsideAngular(() => {
      this.map.on('mouseenter', layerId, () => {
        this.map.getCanvas().style.cursor = 'pointer'
      })
      this.map.on('mouseleave', layerId, () => {
        this.map.getCanvas().style.cursor = ''
      })
    })
  }

  getSource(id: string) {
    return this.map.getSource(id)
  }

  addSource(id: string, data: any) {
    this.zone.runOutsideAngular(() => {
      const sourceData = {
        type: 'geojson',
        data: data,
      }
      this.map.addSource(id, sourceData)
    })
  }

  updateSource(id: string, data: any) {
    this.zone.runOutsideAngular(() => {
      this.map.getSource(id).setData(data)
    })
  }

  removeSource(id: string) {
    this.zone.runOutsideAngular(() => {
      if (this.map.getSource(id)) {
        this.map.removeSource(id)
      }
    })
  }

  fetchBigqueryTileset({
    tileset,
    region = 'us',
    user,
    format = 'tilejson',
    apikey = 'default_public',
  }) {
    const base = `https://maps-api-v2.${region}.carto.com`
    const url = `${base}/user/${user}/bigquery/tileset?source=${tileset}&format=${format}&api_key=${apikey}`
    return firstValueFrom(this.http.get(url))
  }

  createMVTSource(source: IMVTSourceConfig) {
    return new carto.source.MVT(
      source.url,
      {
        properties: { ...source.properties },
        idProperty: source.idProperty || 'id',
        isAggregated: source.isAggregated || false,
      },
      {
        layerID: source.layerName || 'cartoLayer',
        viewportZoomToSourceZoom: Math.floor,
      },
    )
  }

  createGeoJSONSource(data) {
    return new carto.source.GeoJSON(data)
  }

  updateViz(id: string, viz: string) {
    return this.zone.runOutsideAngular(() => {
      const newViz = new carto.Viz(viz)
      const layer = this.getLayer(id)
      return layer.update(layer._source, newViz)
    })
  }

  updateVectorTileSource(
    layerId: string,
    tiles,
    properties,
    viz?,
    idProperty?,
  ): Promise<any> {
    return this.zone.runOutsideAngular(() => {
      const source = this.createMVTSource({
        url: tiles,
        properties,
        idProperty,
      })
      const layer = this.getLayer(layerId)
      if (!layer) {
        return
      }

      const _viz = viz ? new carto.Viz(viz) : layer._viz
      return layer.update(source, _viz)
    })
  }

  updateGeoJSONSource(layerId: string, data, viz?): Promise<any> {
    return this.zone.runOutsideAngular(() => {
      const source = this.createGeoJSONSource(data)
      const layer = this.getLayer(layerId)
      const _viz = viz ? new carto.Viz(viz) : layer._viz
      return layer.update(source, _viz)
    })
  }

  addLayer(layerConfig: ICartoLayerConfig) {
    this.zone.runOutsideAngular(() => {
      const viz =
        typeof layerConfig.viz === 'string'
          ? new carto.Viz(layerConfig.viz)
          : layerConfig.viz
      const layer = new carto.Layer(layerConfig.id, layerConfig.source, viz)
      this.layersTree[layerConfig.id] = {
        layer,
        interactivity: null,
        events: {},
      }
      this.loadLayer(layer, layerConfig.before)
      if (layerConfig.events) {
        Object.keys(layerConfig.events).forEach((key) => {
          this.addLayerEvent(layerConfig.id, key, layerConfig.events[key])
        })
      }
    })
  }

  addPointLayer(
    id: string,
    data: any,
    layout: any,
    styles: any = null,
    filter: any[] = null,
    before: string = null,
  ) {
    this.zone.runOutsideAngular(() => {
      const layerInfo = {
        id: id,
        type: 'symbol',
        source: data,
        layout: layout,
      }
      if (styles) {
        layerInfo['paint'] = styles
      }
      if (filter) {
        layerInfo['filter'] = filter
      }
      this.loadLayer(layerInfo, before)

      this.setupLayerCursor(id)
    })
  }

  addLineLayer(
    id: string,
    data: any,
    styles: any,
    filter: any[] = null,
    before: string = null,
  ) {
    this.zone.runOutsideAngular(() => {
      const layerInfo = {
        id: id,
        type: 'line',
        source: data,
        layout: {},
        paint: styles,
      }
      if (filter) {
        layerInfo['filter'] = filter
      }
      this.loadLayer(layerInfo, before)

      this.setupLayerCursor(id)
    })
  }

  addPolygonLayer(
    id: string,
    data: any,
    styles: any,
    filter: any[] = null,
    before: string = null,
    extrude = false,
  ) {
    this.zone.runOutsideAngular(() => {
      const layerInfo = {
        id: id,
        type: extrude ? 'fill-extrusion' : 'fill',
        source: data,
        layout: {},
        paint: styles,
      }
      if (filter) {
        layerInfo['filter'] = filter
      }
      this.loadLayer(layerInfo, before)

      this.setupLayerCursor(id)
    })
  }

  addCircleLayer(
    id: string,
    data: any,
    styles: any,
    filter: any[] = null,
    before: string = null,
  ) {
    this.zone.runOutsideAngular(() => {
      const layerInfo = {
        id: id,
        type: 'circle',
        source: data,
        layout: {},
        paint: styles,
      }
      if (filter) {
        layerInfo['filter'] = filter
      }
      this.loadLayer(layerInfo, before)
    })
  }

  moveLayer(id: string, before: string = null) {
    this.zone.runOutsideAngular(() => {
      if (this.map.getLayer(id)) {
        this.map.moveLayer(id, before)
      }
    })
  }

  removeLayer(id: string) {
    this.zone.runOutsideAngular(() => {
      if (this.map.getLayer(id)) {
        this.map.removeLayer(id)
      }
    })
  }

  removeCartoLayer(id: string) {
    this.zone.runOutsideAngular(() => {
      const model = this.layersTree[id]
      if (!model) {
        return
      }
      Object.keys(model.events).forEach((key) => {
        model.interactivity.off(key, model.events[key])
      })
      model.layer.remove()
      delete this.layersTree[id]
    })
  }

  hideLayer(id: string) {
    this.zone.runOutsideAngular(() => {
      this.map.setLayoutProperty(id, 'visibility', 'none')
    })
  }

  showLayer(id: string) {
    this.zone.runOutsideAngular(() => {
      this.map.setLayoutProperty(id, 'visibility', 'visible')
    })
  }

  hideCartoLayer(layerId: string) {
    this.zone.runOutsideAngular(() => {
      const layer = this.getLayer(layerId)
      if (layer) {
        layer.hide()
      }
    })
  }

  showCartoLayer(layerId: string) {
    this.zone.runOutsideAngular(() => {
      const layer = this.getLayer(layerId)
      if (layer) {
        layer.show()
      }
    })
  }

  setLayerStyle(id: string, style: any) {
    this.zone.runOutsideAngular(() => {
      for (const prop in style) {
        if (prop) {
          this.map.setPaintProperty(id, prop, style[prop])
        }
      }
    })
  }

  private loadLayer(layerInfo: any, before: string = null) {
    this.zone.runOutsideAngular(() => {
      if (this.isMapLoaded) {
        if (typeof layerInfo.addTo === 'function') {
          layerInfo.addTo(this.map, before)
        } else {
          this.map.addLayer(layerInfo, before)
        }
      } else {
        this.layersQueue.push(layerInfo)
      }
    })
  }

  addLayerEvent(layerId: string, event: string, listener: Function) {
    this.zone.runOutsideAngular(() => {
      const model = this.layersTree[layerId]
      model.interactivity =
        model.interactivity || new carto.Interactivity(model.layer)
      if (model.events[event]) {
        this.removeLayerEvent(layerId, event)
      }
      this.layersTree[layerId].events[event] = listener
      model.interactivity.on(event, listener)
    })
  }

  removeLayerEvent(layerId: string, event: string) {
    this.zone.runOutsideAngular(() => {
      const model = this.layersTree[layerId]
      const listener = model.events[event]
      model.interactivity.off(event, listener)
      delete this.layersTree[layerId].events[event]
    })
  }

  getFilter(layer: string) {
    return this.map.getFilter(layer)
  }

  setFilter(layer: string, filter: any[]) {
    this.zone.runOutsideAngular(() => {
      this.map.setFilter(layer, filter)
    })
  }

  flyTo(options: any) {
    this.zone.runOutsideAngular(() => {
      this.map.flyTo(options)
    })
  }

  flyToBbox(bbox, options) {
    this.zone.runOutsideAngular(() => {
      this.map.fitBounds(bbox, options)
    })
  }

  addImage(name, url) {
    return this.zone.runOutsideAngular(() => {
      return new Promise((resolve, reject) => {
        this.map.loadImage(url, (error, image) => {
          if (error) {
            reject(error)
          }
          this.map.addImage(name, image)
          resolve(image)
        })
      })
    })
  }

  setMapStyle(mapStyle: any) {
    this.zone.runOutsideAngular(() => {
      if (this.map.style) {
        this.map.style._loaded = false
      }
      this.map.setStyle(mapStyle, { diff: true })
    })
  }

  zoomIn() {
    this.map.zoomIn()
  }

  zoomOut() {
    this.map.zoomOut()
  }

  getBBOX() {
    const bounds = this.map.getBounds(),
      sw = bounds.getSouthWest(),
      ne = bounds.getNorthEast()
    return bboxPolygon([sw.lng, sw.lat, ne.lng, ne.lat])
  }

  getMapPosition() {
    return {
      center: this.map.getCenter(),
      zoom: this.map.getZoom(),
      pitch: this.map.getPitch(),
      bearing: this.map.getBearing(),
    }
  }

  invalidateSize() {
    this.zone.runOutsideAngular(() => {
      this.map.resize()
    })
  }

  clearPopups() {
    this.zone.runOutsideAngular(() => {
      if (this.popupClick) {
        this.popupClick._onClickClose()
      }
      if (this.popupHover) {
        this.popupHover._onClickClose()
      }
    })
  }

  getImage() {
    return this.map.getCanvas().toDataURL('image/png')
  }
}
